/**
 * Shared persistence utilities for onboarding page connection data.
 *
 * Centralizes the logic for saving/restoring page connection state to/from
 * localStorage. Used by StepConnectPages, StepConnectNatives, StepConnectExtensions,
 * and StepSummary to avoid code duplication.
 *
 * NOTE: This module only handles page connection data (connectedPages, selectedPageIds,
 * preExistingPageIds, newlyConnectedPageIds, nativeConnections). Component-specific
 * data like selectedNetworks, selectedFeatures, network index, and OAuth redirect
 * logic must be handled by each component individually.
 */

// localStorage keys for page connection data
const STORAGE_KEYS = {
  CONNECTED_PAGES: 'onboarding_connected_pages',
  SELECTED_PAGE_IDS: 'onboarding_selected_page_ids',
  PRE_EXISTING_PAGE_IDS: 'onboarding_pre_existing_page_ids',
  NEWLY_CONNECTED_PAGE_IDS: 'onboarding_newly_connected_page_ids',
  NATIVE_CONNECTIONS: 'onboarding_native_connections'
}

/**
 * Persist onboarding page connection data to individual localStorage keys.
 * Saves: connectedPages, selectedPageIds, preExistingPageIds,
 *        newlyConnectedPageIds, nativeConnections.
 *
 * @param {Object} store - Vuex store instance (this.$store)
 */
export function persistOnboardingPages(store) {
  const state = store.state.onboarding
  localStorage.setItem(STORAGE_KEYS.CONNECTED_PAGES, JSON.stringify(state.connectedPages))
  localStorage.setItem(STORAGE_KEYS.SELECTED_PAGE_IDS, JSON.stringify(state.selectedPageIds))
  localStorage.setItem(STORAGE_KEYS.PRE_EXISTING_PAGE_IDS, JSON.stringify(state.preExistingPageIds))
  localStorage.setItem(STORAGE_KEYS.NEWLY_CONNECTED_PAGE_IDS, JSON.stringify(state.newlyConnectedPageIds))
  localStorage.setItem(STORAGE_KEYS.NATIVE_CONNECTIONS, JSON.stringify(state.nativeConnections))
}

/**
 * Restore onboarding page connection data from individual localStorage keys.
 * Commits to Vuex store using proper mutations (SET_CONNECTED_PAGES,
 * SET_ALL_SELECTED_PAGE_IDS, SET_PRE_EXISTING_PAGE_IDS,
 * SET_NEWLY_CONNECTED_PAGE_IDS, SET_NATIVE_CONNECTIONS).
 *
 * @param {Object} store - Vuex store instance (this.$store)
 * @returns {boolean} true if state was found and restored, false if no saved state exists
 */
export function restoreOnboardingPages(store) {
  const savedConnectedPages = localStorage.getItem(STORAGE_KEYS.CONNECTED_PAGES)
  const savedSelectedPageIds = localStorage.getItem(STORAGE_KEYS.SELECTED_PAGE_IDS)
  const savedPreExistingPageIds = localStorage.getItem(STORAGE_KEYS.PRE_EXISTING_PAGE_IDS)
  const savedNewlyConnectedPageIds = localStorage.getItem(STORAGE_KEYS.NEWLY_CONNECTED_PAGE_IDS)
  const savedNativeConnections = localStorage.getItem(STORAGE_KEYS.NATIVE_CONNECTIONS)

  if (!savedConnectedPages && !savedSelectedPageIds) {
    return false
  }

  try {
    if (savedConnectedPages) {
      store.commit('onboarding/SET_CONNECTED_PAGES', JSON.parse(savedConnectedPages))
    }
    if (savedSelectedPageIds) {
      store.commit('onboarding/SET_ALL_SELECTED_PAGE_IDS', JSON.parse(savedSelectedPageIds))
    }
    if (savedPreExistingPageIds) {
      const preExistingPageIds = JSON.parse(savedPreExistingPageIds)
      Object.entries(preExistingPageIds).forEach(([network, pageIds]) => {
        store.commit('onboarding/SET_PRE_EXISTING_PAGE_IDS', { network, pageIds })
      })
    }
    if (savedNewlyConnectedPageIds) {
      store.commit('onboarding/SET_NEWLY_CONNECTED_PAGE_IDS', JSON.parse(savedNewlyConnectedPageIds))
    }
    if (savedNativeConnections) {
      store.commit('onboarding/SET_NATIVE_CONNECTIONS', JSON.parse(savedNativeConnections))
    }
    return true
  } catch (error) {
    console.error('[onboardingPersistence] Error restoring page data:', error)
    return false
  }
}

/**
 * All localStorage keys used by the onboarding flow.
 * Used by clearOnboardingFlags() to clean up on exit/skip/finish.
 */
const ALL_ONBOARDING_KEYS = [
  'onboarding_mode', 'onboarding_current_network', 'onboarding_current_index',
  'onboarding_selected_networks', 'onboarding_selected_features',
  'onboarding_connected_pages', 'onboarding_selected_page_ids',
  'onboarding_pre_existing_page_ids', 'onboarding_newly_connected_page_ids',
  'onboarding_native_connections', 'onboarding_summary_refresh',
  'onboarding_summary_state', 'onboarding_oauth_complete',
  'onboarding_state', 'onboarding_native_state',
  'onboarding_pages_before_extension',
  'mashup_extension_return', 'mashup_onboarding_context'
]

/**
 * Clear all onboarding-related localStorage flags.
 * Called when user exits, skips, or finishes onboarding.
 */
export function clearOnboardingFlags() {
  ALL_ONBOARDING_KEYS.forEach(key => localStorage.removeItem(key))
}
